/* eslint-disable @typescript-eslint/no-unused-vars */
import React, {
  ForwardedRef,
  forwardRef,
  useEffect,
  useRef,
  useState,
} from "react";

import useArticleSection from "@/components/business/FillInTableContainer/hooks/useArticleSection";
import { useArticleInfoQuery } from "@api/articleInfo/useArticleInfo";
import { IS_NOT_EDITABLE_SHELL_KEYS } from "@FillInTableContainer/components/FillInTable/constants/disabledShellBySection.constants";
import TableCreatorListBox from "@FillInTableContainer/shared/components/TableCreatorListBox/TableCreatorListBox";
import { AUTO_COMPANY_SHELL } from "@FillInTableContainer/shared/constants/autoCompleteListShell";
import { ENABLE_GROUP_TABLE_KEY_LIST } from "@FillInTableContainer/shared/constants/tableCreatorGroupTable.constants";
import { useTableCreatorFocusStatus } from "@FillInTableContainer/shared/context/TableCreatorFocusStatusContext";
import { useTableCreatorPositionInfo } from "@FillInTableContainer/shared/context/TableCreatorPositionInfoContext";
import { useHandleTextFieldInputEvent } from "@FillInTableContainer/shared/hooks/useHandleTextFieldInputEvent";
import { getPressIdFromURL } from "@utils/getStateFromURL";

import CopyButton from "./components/CopyButton/CopyButton";
import DeleteButton from "./components/DeleteButton/DeleteButton";
import ValidationMessage from "./components/ValidationMessage/ValidationMessage";
import * as S from "./TableCreatorTextFieldInput.style";

import type { EntryDataValueAndOptions } from "@/types/clinicalTrials.types";
import type { GroupTableKeyType } from "@FillInTableContainer/shared/types/tableCreator.types";

interface Props {
  placeholder: string;
  shellValue: EntryDataValueAndOptions;
  shellKey: string;
  groupTableKey: GroupTableKeyType;
  groupTableIndex: number;
  groupShellKey: string | null;
  tableKey: string | null;
  tableIndex: number;
  groupShellIndex: number;
  subGroupShellKey: string | null;
  subGroupShellIndex: number | null;
  shellIndex: number;
  valueIndex: number;
  changeTextFieldMarginBottom: (margin: number) => void;
  isMultipleLastValue: boolean;
  isMultipleValue?: boolean;
  openValidationInfo: () => void;
  // eslint-disable-next-line react/no-unused-prop-types
  closeValidationInfo: () => void;
}

const TableCreatorTextFieldInput = forwardRef(
  (
    {
      placeholder,
      shellValue,
      shellKey,
      groupTableKey,
      groupTableIndex,
      groupShellKey,
      tableKey,
      tableIndex,
      groupShellIndex,
      subGroupShellKey,
      subGroupShellIndex,
      shellIndex,
      valueIndex,
      changeTextFieldMarginBottom,
      isMultipleLastValue,
      isMultipleValue,
      openValidationInfo,
    }: Props,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const { info } = useTableCreatorPositionInfo();
    const { focusStatus } = useTableCreatorFocusStatus();
    const positionInfo = {
      groupTableKey: groupTableKey as GroupTableKeyType,
      groupTableIndex,
      tableKey,
      tableIndex,
      groupShellKey,
      groupShellIndex,
      subGroupShellKey,
      subGroupShellIndex,
      shellKey,
      shellIndex,
      valueIndex,
    };

    const isFocusedShell =
      info.tableIndex === tableIndex &&
      info.groupShellKey === groupShellKey &&
      info.groupShellIndex === groupShellIndex &&
      info.shellKey === shellKey &&
      info.shellIndex === shellIndex &&
      info.valueIndex === valueIndex;

    const {
      textFieldTextareaRef,
      targetListIndex,
      selectedValue,
      validationMessage,
      valueInfo,
      aiList,
      dictionaryList,
      dataSetList,
      isDictionaryList,
      isFocusInputField,
      isDataSetList,
      copyStatus,
      copyCurrentTextValue,
      checkIndexOfValueInDefaultAiList,
      changeSelectedValue,
      handleEnterValueKeyboard,
      handleChangedEnterValue,
      handleFocusTextField,
      handleFocusOutTextField,
      handleBlurTextField,
      deleteCurrentTextField,
      handleChangeDataSetValue,
    } = useHandleTextFieldInputEvent(positionInfo, shellValue);

    const draftArticleId = getPressIdFromURL();
    const articleSection = useArticleSection();
    const [isTextDragging, setIsTextDragging] = useState(false);
    const cursorPositionRef = useRef<number | null>(null);

    const { data: articleInfo } = useArticleInfoQuery(Number(draftArticleId));

    const enableGroupTableKeyList = ENABLE_GROUP_TABLE_KEY_LIST[articleSection];

    const isNotEditable =
      IS_NOT_EDITABLE_SHELL_KEYS[articleSection].includes(shellKey);

    const isEnableEdit = enableGroupTableKeyList[
      groupTableKey as keyof typeof enableGroupTableKeyList
    ].includes(articleInfo?.draftArticleStatus ?? "");

    useEffect(() => {
      const handleCheckClickOutside = () => {
        if (isFocusInputField && !isTextDragging) {
          handleFocusOutTextField();
        }
      };
      window.addEventListener("click", handleCheckClickOutside);

      return () => window.removeEventListener("click", handleCheckClickOutside);
    }, [handleFocusOutTextField, isFocusInputField, isTextDragging]);

    const saveCursorPosition = () => {
      try {
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          if (
            textFieldTextareaRef.current &&
            textFieldTextareaRef.current.contains(range.startContainer)
          ) {
            const textLength =
              textFieldTextareaRef.current.textContent?.length || 0;
            cursorPositionRef.current = Math.min(range.startOffset, textLength);
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("커서 위치 저장 중 오류 발생:", error);
      }
    };

    const restoreCursorPosition = () => {
      try {
        if (
          textFieldTextareaRef.current &&
          cursorPositionRef.current !== null
        ) {
          const selection = window.getSelection();
          if (selection) {
            const currentPosition =
              selection.rangeCount > 0
                ? selection.getRangeAt(0).startOffset
                : 0;

            if (currentPosition === 0) {
              if (
                !textFieldTextareaRef.current.firstChild &&
                textFieldTextareaRef.current.textContent === ""
              ) {
                textFieldTextareaRef.current.textContent = "";
              }

              const textNode =
                textFieldTextareaRef.current.firstChild ||
                textFieldTextareaRef.current;

              const textLength = textNode.textContent?.length || 0;
              const position = Math.min(cursorPositionRef.current, textLength);

              const range = document.createRange();
              range.setStart(textNode, position);
              range.setEnd(textNode, position);
              selection.removeAllRanges();
              selection.addRange(range);
            }
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("커서 위치 복원 중 오류 발생:", error);
      }
    };

    if (isNotEditable) {
      return (
        <S.TextFieldInputWrapper className="modality-textfield-height">
          <S.TextareaWrapper className="modality-textfield-height">
            <S.TextFieldTextarea
              placeholder={placeholder}
              defaultValue={selectedValue}
              ref={textFieldTextareaRef}
              $isModified={valueInfo.is_modified ?? true}
              $isMultipleLastValue={isMultipleLastValue}
              $disabled
              $isNotEditable={isNotEditable}
              $isFocusedShell={false}
              className="modality-textfield-height"
            />
          </S.TextareaWrapper>
        </S.TextFieldInputWrapper>
      );
    }

    return (
      <S.TextFieldInputWrapper className="modality-textfield-height">
        <S.TextareaWrapper className="modality-textfield-height">
          <S.TextFieldTextarea
            onFocus={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleFocusTextField(e);
              setIsTextDragging(true);
              setTimeout(restoreCursorPosition, 0);
            }}
            onBlur={() => {
              saveCursorPosition();
              handleBlurTextField();
              setIsTextDragging(false);
            }}
            onInput={(e) => {
              handleChangedEnterValue(e);
              setTimeout(saveCursorPosition, 0);
            }}
            onKeyDown={handleEnterValueKeyboard}
            onMouseUp={() => {
              saveCursorPosition();
            }}
            onKeyUp={() => {
              saveCursorPosition();
            }}
            onClick={(event) => {
              event.stopPropagation();
            }}
            placeholder={placeholder}
            defaultValue={selectedValue}
            ref={textFieldTextareaRef}
            contentEditable={isEnableEdit}
            $isModified={valueInfo.is_modified ?? true}
            $isMultipleLastValue={isMultipleLastValue}
            $isFocusedShell={isFocusedShell}
            $disabled={!isEnableEdit}
            $isNotEditable={isNotEditable}
            className="modality-textfield-height"
          />
        </S.TextareaWrapper>
        {valueInfo.text && (
          <CopyButton
            copyStatus={copyStatus}
            copyCurrentTextValue={copyCurrentTextValue}
          />
        )}
        {valueInfo.text && (
          <DeleteButton deleteCurrentTextField={deleteCurrentTextField} />
        )}
        {isFocusInputField &&
          !AUTO_COMPANY_SHELL.includes(shellKey) &&
          focusStatus.container !== "noFocus" && (
            <TableCreatorListBox
              ref={ref}
              aiList={aiList}
              shellKey={shellKey}
              changeSelectedValue={changeSelectedValue}
              changeTextFieldMarginBottom={changeTextFieldMarginBottom}
              targetListIndex={targetListIndex}
              dictionaryList={dictionaryList}
              isDictionaryList={isDictionaryList}
              isDataSetList={isDataSetList}
              dataSetList={dataSetList}
              checkIndexOfValueInDefaultAiList={
                checkIndexOfValueInDefaultAiList
              }
              handleChangeDataSetValue={handleChangeDataSetValue}
            />
          )}
        {validationMessage !== null && !!selectedValue && (
          <ValidationMessage
            messageInfo={validationMessage}
            openValidationInfo={openValidationInfo}
            isMultipleValue={isMultipleValue}
          />
        )}
      </S.TextFieldInputWrapper>
    );
  },
);

export default TableCreatorTextFieldInput;
